import {
  Avatar,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Stack,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { useAuthAdmin } from '../AuthProviderAdmin';

interface BulkAssignModalProps {
  isOpen: boolean;
  onClose: () => void;
  taskIds: string[];
}

export function BulkAssignModal({ isOpen, onClose, taskIds }: BulkAssignModalProps) {
  const utils = trpcAdmin.useUtils();
  const toast = useToast();
  const auth = useAuthAdmin();

  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  const { data: users } = trpcAdmin.users.all.useQuery({}, { initialData: [] });

  const { loggedInUser, otherUsers } = useMemo(() => {
    const loggedInUserIndex = users.findIndex((user) => user.id === auth?.user?.id);
    if (loggedInUserIndex === -1) {
      return { otherUsers: users };
    }

    const otherUsers = [...users];
    const [loggedInUser] = otherUsers.splice(loggedInUserIndex, 1);
    return { loggedInUser, otherUsers };
  }, [users, auth.user]);

  const filteredUsers = useMemo(() => {
    return otherUsers.filter(
      ({ first_name, last_name }) =>
        searchQuery === '' ||
        `${first_name} ${last_name}`.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [otherUsers, searchQuery]);

  const bulkAssignMutation = trpcAdmin.support.task.bulkAssign.useMutation({
    onSuccess: (data) => {
      const { count, totalRequested, skipped } = data;

      // If all tasks were assigned successfully
      if (count === totalRequested) {
        toast({
          status: 'success',
          title: t`Successfully assigned ${count} tasks`,
        });
      }
      // If some tasks were assigned
      else if (count > 0) {
        toast({
          status: 'success',
          title: t`Successfully assigned ${count} of ${totalRequested} tasks`,
        });
        toast({
          status: 'warning',
          title: t`${skipped} tasks could not be assigned`,
          description: t`Tasks with COMPLETED status cannot be reassigned`,
        });
      }
      // If no tasks were assigned
      else {
        toast({
          status: 'warning',
          title: t`No tasks were assigned`,
          description: t`All selected tasks have COMPLETED status and cannot be reassigned`,
        });
      }

      void utils.support.task.list.invalidate(undefined, { refetchType: 'active' });
      handleClose();
    },
    onError: (error) => {
      toast({
        status: 'error',
        title: t`Failed to assign tasks`,
        description: error.message,
      });
    },
  });

  const handleClose = () => {
    setSelectedUserId('');
    setSearchQuery('');
    onClose();
  };

  const handleSubmit = () => {
    if (!selectedUserId) {
      toast({
        status: 'error',
        title: t`Please select a user`,
      });
      return;
    }

    const selectedUser = users.find((u) => u.id === selectedUserId);
    const selectedUserDrn = selectedUser?.drn;
    if (selectedUserDrn === undefined) {
      toast({
        status: 'error',
        title: t`Selected user has no DRN`,
      });
      return;
    }

    bulkAssignMutation.mutate({
      taskIds,
      assignedToDrn: selectedUserDrn,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Trans>Bulk Assign Tasks</Trans>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <Text fontSize="sm" color="fg.muted">
              <Trans>Assigning {taskIds.length} tasks</Trans>
            </Text>

            <FormControl>
              <FormLabel>
                <Trans>Assign to</Trans>
              </FormLabel>
              <RadioGroup value={selectedUserId} onChange={setSelectedUserId}>
                <Stack spacing={2}>
                  {loggedInUser ? (
                    <Radio value={loggedInUser.id}>
                      <HStack spacing={2}>
                        <Avatar
                          size="xs"
                          name={`${loggedInUser.first_name} ${loggedInUser.last_name}`}
                        />
                        <Text fontSize="sm">
                          {loggedInUser.first_name} {loggedInUser.last_name}{' '}
                          <Text as="span" color="fg.muted">
                            (me)
                          </Text>
                        </Text>
                      </HStack>
                    </Radio>
                  ) : null}

                  {filteredUsers.length > 0 && (
                    <>
                      <InputGroup size="sm" mt={2}>
                        <InputLeftElement>
                          <Search size="14px" />
                        </InputLeftElement>
                        <Input
                          placeholder={t`Search users...`}
                          type="search"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                      </InputGroup>

                      <VStack align="stretch" spacing={1} maxH="300px" overflowY="auto" pt={2}>
                        {filteredUsers.map((user) => (
                          <Radio key={user.id} value={user.id}>
                            <HStack spacing={2}>
                              <Avatar size="xs" name={`${user.first_name} ${user.last_name}`} />
                              <Text fontSize="sm">
                                {user.first_name} {user.last_name}
                              </Text>
                            </HStack>
                          </Radio>
                        ))}
                      </VStack>
                    </>
                  )}
                </Stack>
              </RadioGroup>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={2}>
            <Button variant="ghost" onClick={handleClose}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSubmit}
              isLoading={bulkAssignMutation.isPending}
              isDisabled={!selectedUserId}
            >
              <Trans>Assign Tasks</Trans>
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
